import { chakra } from '@chakra-ui/react';
import gql from 'graphql-tag';
import { useCallback, useMemo, useState } from 'react';
import Select, { ActionMeta, StylesConfig } from 'react-select';
import { useGetOrganizationsQuery } from './__generated__/OrganizationSelect.graphql';

export interface OrganizationType {
  value: string;
  label: string | undefined;
}

const emptyOrganization: OrganizationType = {
  value: '',
  label: '',
};

interface props {
  onChange: (value: OrganizationType) => void;
  tabIndex: number;
  organization?: string;
}

export function OrganizationSelect({ onChange, tabIndex }: props) {
  const { error, data } = useGetOrganizationsQuery();
  const organizations = data?.organizations;
  const OrganizationOptions: OrganizationType[] = (organizations ?? []).map((organization) => ({
    value: organization.id,
    label: organization.name,
  }));
  const defaultOrganization: OrganizationType = {
    value: '',
    label: '',
  };
  const [selectedOrganization, setSelectedOrganization] = useState(defaultOrganization);
  const currentOrganization = useMemo(() => {
    return OrganizationOptions.find((option) => option.value === selectedOrganization.value);
  }, [OrganizationOptions, selectedOrganization.value]);

  const formatOptionLabel = useCallback((data: OrganizationType) => {
    return <chakra.span>{data.label}</chakra.span>;
  }, []);

  const handleChange = async (
    option: OrganizationType | null,
    meta: ActionMeta<OrganizationType>,
  ) => {
    if (meta.action === 'clear') {
      setSelectedOrganization(emptyOrganization);
      onChange(emptyOrganization);
    } else {
      setSelectedOrganization(option ?? emptyOrganization);
      onChange(option ?? emptyOrganization);
    }
  };
  const customStyles: StylesConfig<OrganizationType, false> = {
    placeholder: (base) => ({
      ...base,
      fontSize: '22px',
      color: '#333',
    }),
  };
  if (error) {
    return (
      <chakra.div color="red.500" fontSize="sm" p={2}>
        Failed to load organizations. Please try again later.
      </chakra.div>
    );
  }
  return (
    <Select
      value={currentOrganization}
      options={OrganizationOptions}
      tabIndex={tabIndex}
      onChange={handleChange}
      formatOptionLabel={formatOptionLabel}
      placeholder={'- - -'}
      isLoading={false}
      isDisabled={false}
      isMulti={false}
      escapeClearsValue={true}
      isClearable={false}
      isSearchable={true}
      menuPlacement="auto"
      styles={customStyles}
    />
  );
}

OrganizationSelect.graphql = {
  queries: {
    GetOrganizations: gql`
      query GetOrganizations {
        organizations {
          id
          name
          handle
        }
      }
    `,
  },
};
